import React from 'react';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import { 
  GraduationCap, 
  BookOpen, 
  Users, 
  Award, 
  CheckCircle, 
  ArrowRight,
  Clock,
  Globe,
  Target,
  BarChart3,
  Shield,
  Zap,
  MessageSquare,
  Video,
  Headphones
} from 'lucide-react';

const ProfessionalTraining: React.FC = () => {
  const handleScrollToPrograms = () => {
    const el = document.getElementById('training-programs');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  const trainingPrograms = [
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Dynamics 365 Training",
      description: "Comprehensive training programs for Dynamics 365 Sales, Customer Service, and Finance & Operations.",
      modules: ["User training", "Administrator training", "Developer training", "Customization training"],
      duration: "2-5 days",
      level: "Beginner to Advanced"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Power Platform Training",
      description: "Learn to build solutions with Power Apps, Power BI, Power Automate, and Power Virtual Agents.",
      modules: ["Power Apps development", "Power BI analytics", "Power Automate workflows", "Power Virtual Agents"],
      duration: "3-7 days",
      level: "Beginner to Intermediate"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Business Process Training",
      description: "Training on business process optimization, change management, and digital transformation.",
      modules: ["Process mapping", "Change management", "Digital transformation", "Best practices"],
      duration: "2-4 days",
      level: "Intermediate to Advanced"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Security & Compliance",
      description: "Training on Microsoft 365 security, compliance, and data protection best practices.",
      modules: ["Security fundamentals", "Compliance frameworks", "Data protection", "Risk management"],
      duration: "2-3 days",
      level: "Intermediate"
    }
  ];

  const benefits = [
    "Certified Microsoft trainers with real-world experience",
    "Hands-on practical exercises and real scenarios",
    "Flexible training formats (in-person, virtual, hybrid)",
    "Customized training programs for your organization",
    "Post-training support and resources",
    "Certification preparation and exam guidance"
  ];

  const trainingFormats = [
    {
      title: "In-Person Training",
      description: "Face-to-face training at your location or our training center with hands-on labs.",
      icon: <Users className="w-8 h-8" />,
      features: ["Direct interaction", "Hands-on labs", "Group discussions", "Immediate feedback"]
    },
    {
      title: "Virtual Training",
      description: "Live online training sessions with interactive exercises and real-time support.",
      icon: <Globe className="w-8 h-8" />,
      features: ["Live instructor", "Screen sharing", "Virtual labs", "Recorded sessions"]
    },
    {
      title: "Hybrid Training",
      description: "Combination of in-person and virtual sessions for maximum flexibility and engagement.",
      icon: <Target className="w-8 h-8" />,
      features: ["Flexible scheduling", "Mixed delivery", "Cost effective", "Wide reach"]
    },
    {
      title: "Self-Paced Learning",
      description: "On-demand courses with video tutorials, documentation, and assessment tools.",
      icon: <Clock className="w-8 h-8" />,
      features: ["Learn at your pace", "24/7 access", "Progress tracking", "Certification prep"]
    }
  ];

  const certifications = [
    {
      title: "Microsoft Dynamics 365",
      exams: ["MB-200", "MB-210", "MB-220", "MB-230"],
      description: "Official Microsoft certifications for Dynamics 365 professionals."
    },
    {
      title: "Power Platform",
      exams: ["PL-100", "PL-200", "PL-400", "PL-500"],
      description: "Microsoft Power Platform certifications for developers and administrators."
    },
    {
      title: "Microsoft 365",
      exams: ["MS-900", "MS-500", "MS-700", "MS-720"],
      description: "Microsoft 365 certifications for security, compliance, and administration."
    }
  ];

  const testimonials = [
    {
      quote: "The training program was excellent. Our team is now confident in using Dynamics 365 effectively.",
      author: "David Thompson",
      position: "IT Manager",
      company: "Innovation Corp"
    },
    {
      quote: "Professional, engaging, and practical training that delivered immediate value to our organization.",
      author: "Lisa Chen",
      position: "HR Director",
      company: "Tech Solutions Ltd"
    },
    {
      quote: "The Power Platform training helped us build custom solutions without extensive coding knowledge.",
      author: "Robert Wilson",
      position: "Business Analyst",
      company: "Global Enterprises"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      <SEOHead 
        title="Professional Training - Microsoft Dynamics 365 & Power Platform Courses"
        description="Comprehensive professional training programs for Microsoft Dynamics 365, Power Platform, Azure, and more. Expert-led courses to enhance your team's skills and capabilities."
        keywords="Dynamics 365 training, Power Platform training, Microsoft training, professional development, Azure training, CRM training"
        canonical="/professional-training"
      />
             {/* Hero Section */}
       <section className="pt-48 pb-20 bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex items-center justify-center mb-6">
              <GraduationCap className="w-12 h-12 mr-4" />
              <h1 className="text-5xl font-bold">Professional Training</h1>
            </div>
            <p className="text-xl text-green-100 mb-8">
              Empower your team with expert training on Microsoft technologies. Our comprehensive 
              training programs help organizations maximize their technology investments and drive success.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-green-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-50 transition-all duration-300"
              onClick={handleScrollToPrograms}
            >
              Explore Training Programs
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Training Programs */}
      <section id="training-programs" className="py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Training Programs
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive training programs designed to equip your team with the skills and knowledge 
              needed to succeed with Microsoft technologies.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {trainingPrograms.map((program, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="text-green-600 mb-4">{program.icon}</div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-3">
                  {program.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {program.description}
                </p>
                <div className="space-y-2 mb-6">
                  {program.modules.map((module, moduleIndex) => (
                    <div key={moduleIndex} className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{module}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>Duration: {program.duration}</span>
                  <span>Level: {program.level}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why Choose Our Training?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We provide high-quality training that delivers real value and measurable results for your organization.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center space-x-4"
              >
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                <span className="text-lg text-gray-700">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Formats */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Training Formats
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the training format that best fits your organization's needs and learning preferences.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {trainingFormats.map((format, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-center"
              >
                <div className="text-green-600 mb-4 flex justify-center">{format.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {format.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {format.description}
                </p>
                <div className="space-y-1">
                  {format.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="text-xs text-gray-500">
                      • {feature}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Microsoft Certifications
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Prepare for official Microsoft certifications with our comprehensive training programs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <Award className="w-8 h-8 text-green-600 mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">
                    {cert.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-4">
                  {cert.description}
                </p>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700">Exam Codes:</p>
                  <div className="flex flex-wrap gap-2">
                    {cert.exams.map((exam, examIndex) => (
                      <span key={examIndex} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                        {exam}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What Our Trainees Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from professionals who have enhanced their skills through our training programs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center mb-4">
                  <MessageSquare className="w-6 h-6 text-green-600 mr-2" />
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <Award key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-600 mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-sm text-gray-600">{testimonial.position}</p>
                  <p className="text-sm text-green-600">{testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-green-600 to-blue-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6">
              Ready to Upskill Your Team?
            </h2>
            <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
              Invest in your team's success with our professional training programs. Contact us to 
              discuss your training needs and get a customized proposal.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-green-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-50 transition-all duration-300"
              >
                Request Training Proposal
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-green-600 transition-all duration-300"
              >
                View Course Catalog
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProfessionalTraining; 